import React, { useState, useMemo } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Paper } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import CalendarToolbar from '../../../components/CalendarToolbar';
import 'react-big-calendar/lib/css/react-big-calendar.css';

const localizer = momentLocalizer(moment);

const TYPE_COLORS = {
  quiz: '#3b82f6',
  test: '#8b5cf6',
  homework: '#6366f1',
  lecture: '#10b981',
};

function CourseCourseCalendar({ course, assignments, lectures = [] }) {
  const navigate = useNavigate();
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));

  const [view, setView] = useState(isMobile ? 'agenda' : 'month');
  const [date, setDate] = useState(new Date());

  const calendarHeight = isMobile ? 350 : 600;

  const events = useMemo(() => {
    const items = [];
    assignments.forEach((assignment) => {
      const color = TYPE_COLORS[assignment.type] || '#6366f1';
      if (assignment.due_date) {
        items.push({
          id: assignment.id,
          title: assignment.title,
          start: new Date(assignment.due_date),
          end: new Date(assignment.due_date),
          color,
          type: 'due',
          assignmentType: assignment.type,
          points: assignment.points_possible,
        });
      }
      if (assignment.start_date) {
        items.push({
          id: `start-${assignment.id}`,
          title: `${assignment.title} (Opens)`,
          start: new Date(assignment.start_date),
          end: new Date(assignment.start_date),
          color,
          type: 'start',
          assignmentId: assignment.id,
          assignmentType: assignment.type,
        });
      }
    });
    lectures.forEach((lecture) => {
      if (lecture.scheduled_at) {
        const start = new Date(lecture.scheduled_at);
        const end = lecture.duration_minutes
          ? new Date(start.getTime() + lecture.duration_minutes * 60000)
          : new Date(start.getTime() + 60 * 60000);
        items.push({
          id: `lecture-${lecture.id}`,
          title: lecture.title,
          start,
          end,
          color: TYPE_COLORS.lecture,
          type: 'lecture',
          lectureId: lecture.id,
          assignmentType: 'lecture',
        });
      }
    });
    return items;
  }, [assignments, lectures]);

  const upcomingAssignments = useMemo(() => {
    return assignments
      .filter((a) => a.due_date && new Date(a.due_date) >= new Date())
      .sort((a, b) => new Date(a.due_date) - new Date(b.due_date))
      .slice(0, 10);
  }, [assignments]);

  const handleEventClick = (event) => {
    if (event.type === 'lecture') {
      navigate(`/courses/${course.id}/lectures`);
      return;
    }
    const assignmentId = event.type === 'start' ? event.assignmentId : event.id;
    navigate(`/courses/${course.id}/assignments/${assignmentId}`);
  };

  const eventStyleGetter = (event) => ({
    style: {
      backgroundColor: event.color,
      borderRadius: '4px',
      opacity: event.type === 'start' ? 0.7 : 1,
      color: 'white',
      border: '0px',
      display: 'block',
      fontSize: '0.85rem',
      padding: '2px 4px',
    },
  });

  return (
    <Box sx={{ fontFamily: "'DM Sans', sans-serif" }}>
      {/* Legend */}
      <Paper sx={{ display: 'flex', alignItems: 'center', gap: 2, p: '12px 20px', mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: '12px' }}>
        <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'text.primary' }}>Legend:</Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {Object.entries(TYPE_COLORS).map(([type, color]) => (
            <Box key={type} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Box sx={{ width: 14, height: 14, borderRadius: '4px', bgcolor: color }} />
              <Typography sx={{ fontSize: 13, color: 'text.secondary', fontWeight: 500 }}>
                {type.charAt(0).toUpperCase() + type.slice(1)}
              </Typography>
            </Box>
          ))}
        </Box>
      </Paper>

      {/* Calendar */}
      <Paper
        sx={{
          p: 3,
          mb: 2,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '12px',
          '& .rbc-calendar': { fontFamily: "'DM Sans', sans-serif" },
          '& .rbc-header': { p: '0.75rem 0.5rem', fontWeight: 600, color: 'text.primary', borderBottom: 2, borderColor: 'divider' },
          '& .rbc-today': { bgcolor: 'rgba(99, 102, 241, 0.06)' },
          '& .rbc-off-range-bg': { bgcolor: 'background.default' },
          '& .rbc-event': { cursor: 'pointer', fontSize: { xs: '0.75rem', sm: '0.85rem' }, padding: { xs: '4px 6px', sm: '2px 4px' } },
          '& .rbc-event:hover': { opacity: 0.9 },
        }}
      >
        <Calendar
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          style={{ height: calendarHeight }}
          onSelectEvent={handleEventClick}
          eventPropGetter={eventStyleGetter}
          view={view}
          onView={setView}
          date={date}
          onNavigate={setDate}
          components={{ toolbar: CalendarToolbar }}
          popup
          tooltipAccessor={(event) => `${event.title} (${event.assignmentType})`}
        />
      </Paper>

      {/* Upcoming Deadlines */}
      {upcomingAssignments.length > 0 && (
        <Paper sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: '12px' }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary', mb: 2 }}>Upcoming Deadlines</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {upcomingAssignments.map((assignment) => (
              <Box
                key={assignment.id}
                onClick={() => navigate(`/courses/${course.id}/assignments/${assignment.id}`)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  p: '12px 16px',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover', transform: 'translateX(4px)' },
                }}
              >
                <Box sx={{ width: 4, height: 40, borderRadius: '2px', flexShrink: 0, bgcolor: TYPE_COLORS[assignment.type] || '#6366f1' }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 15, fontWeight: 600, color: 'text.primary', mb: 0.25, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {assignment.title}
                  </Typography>
                  <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase' }}>
                    {assignment.type}
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: 13, fontWeight: 500, color: 'primary.main', fontFamily: "'JetBrains Mono', monospace", flexShrink: 0 }}>
                  {moment(assignment.due_date).format('MMM DD, YYYY')}
                </Typography>
              </Box>
            ))}
          </Box>
        </Paper>
      )}
    </Box>
  );
}

export default CourseCourseCalendar;
